import * as PhosphorIcons from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import VideoCipherPlayer from '../../../components/solution/VideoCipherPlayer';
import YoutubeVideoSolution from '../../../components/solution/VideoSolution';
import PercentileCard from '../PercentileCard';
import CombinedOverallScoreCard from './CombinedOverallScoreCard';
import CombinedStatCard from './CombinedStatCard';
import SubjectScoreCard from './SubjectScoreCard';

export default function CombinedOverview({
  overviewData,
  percentile,
  analysisVideo,
  analysisVideoThumbnail,
  analysisVideoCdn,
  notes,
  packId,
  testId,
  testTitle,
}) {
  const statCards = [
    {
      title: 'LEADERBOARD RANK (COMBINED)',
      icon: 'TrophyIcon',
      color: 'var(--q3-pink-normal)',
      viewButton: true,
      buttonLink: `/v3/${packId}/${testId}/leaderboard?tab=overall-combined`,
      numerator: overviewData?.overallData?.userRank,
      denominator: overviewData?.overallData?.totalRanks,
      tooltipText: 'Click to view Leaderboard',
    },
    {
      title: 'QS ATTEMPTED',
      icon: 'QuestionIcon',
      color: 'var(--q3-sky-normal)',
      numerator: overviewData?.overview?.questionAttempted,
      denominator: overviewData?.overview?.totalQuestions,
      tooltipText: 'Total qs attempted in the test',
    },
    {
      title: 'ACCURACY',
      icon: 'TargetIcon',
      color: 'var(--q3-fuchsia-normal)',
      numerator: overviewData?.overview?.accuracy,
      isPercentage: true,
      tooltipText: 'Overall accuracy of the test',
    },
    {
      title: 'POSITIVE SCORE',
      icon: 'ListChecksIcon',
      color: 'var(--q3-emerald-normal)',
      numerator: overviewData?.overview?.positiveScore,
      denominator: overviewData?.overview?.totalScore,
      tooltipText: 'Score if we ignore incorrect attempts',
    },
    {
      title: 'MARKS LOST',
      icon: 'XSquareIcon',
      color: 'var(--q3-danger-normal)',
      numerator: overviewData?.overview?.marksLost,
      denominator: overviewData?.overview?.totalScore,
      tooltipText: 'Marks lost due to wrong attempts',
    },
  ];

  const subjectWiseData = overviewData?.subjectWiseData;

  const [activeInput, setActiveInput] = useState(null);
  const [learnings, setLearnings] = useState(['', '', '']);

  // Helper function to compact learnings array (move non-empty items to the front)
  const compactLearnings = (arr) => {
    const nonEmpty = arr.filter((item) => item.trim() !== '');
    const empty = arr.filter((item) => item.trim() === '');
    return [...nonEmpty, ...empty].slice(0, 3);
  };

  // Parse initial notes on mount
  useEffect(() => {
    if (notes) {
      const parsed = notes.split('\n');
      const filled = [parsed[0] || '', parsed[1] || '', parsed[2] || ''];
      setLearnings(compactLearnings(filled));
    }
  }, [notes]);

  const renderIcon = (sno) => {
    const iconName = `NumberCircle${sno}Icon`;
    const Icon = PhosphorIcons[iconName];
    return Icon ? <Icon size={24} /> : null;
  };

  return (
    <div className="flex flex-col min-h-full">
      {/* Fixed header section */}
      <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0 md:space-x-4 w-full">
        {overviewData?.overallData && (
          <CombinedOverallScoreCard
            overallData={overviewData?.overallData}
            isPercentileAvailable={
              overviewData?.percentileObject?.isAvailable || overviewData?.percentileObject?.isPercentileDelayed
            }
            paperData={overviewData?.marksBreakdown}
          />
        )}
        {(overviewData?.percentileObject?.isAvailable || overviewData?.percentileObject?.isPercentileDelayed) && (
          <PercentileCard
            percentileData={overviewData?.percentileObject?.percentileData}
            score={overviewData?.overallData?.markScored}
            percentile={overviewData?.percentileObject?.calculatedPercentile}
            isProjected={overviewData?.percentileObject?.isProjected}
            type={overviewData?.percentileObject?.percentileType}
            isPercentileDelayed={overviewData?.percentileObject?.isPercentileDelayed}
            percentileAvailableDate={overviewData?.percentileObject?.percentileAvailableDate}
            isCombined={true}
          />
        )}
      </div>

      {overviewData?.subjectWiseData && (
        <div className="w-full overflow-x-auto mt-4">
          <div className="grid grid-cols-3 gap-3 flex-shrink-0 min-w-max">
            {Object?.values(subjectWiseData)?.map((item) => (
              <SubjectScoreCard
                key={item?.title}
                subject={{
                  title: item?.title,
                  color: item?.color,
                  icon: item?.icon,
                  markScored: item?.markScored,
                  totalScore: item?.totalScore,
                  tooltipText: item?.tooltipText,
                  papers: item?.papers,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Fixed stats section */}
      <div className="my-8 grid grid-cols-1 lg:grid-cols-5 flex-wrap gap-3 flex-shrink-0">
        {statCards.map((item, index) => (
          <CombinedStatCard key={index} cardMetaData={item} />
        ))}
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 min-h-0">
          {analysisVideo && (
            <div className="w-full">
              {analysisVideoCdn === 'yt' ? (
                <YoutubeVideoSolution
                  videoId={analysisVideo}
                  videoThumbnail={analysisVideoThumbnail}
                  isSolution={false}
                  testId={testId}
                  testTitle={testTitle}
                />
              ) : analysisVideoCdn === 'vc' ? (
                <VideoCipherPlayer
                  testId={testId}
                  testTitle={testTitle}
                  videoId={analysisVideo}
                  isSolution={false}
                  videoThumbnail={analysisVideoThumbnail}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
